<template>
  <div
    class="d-flex flex-column w-100 my-3"
    data-testid="list-filter-bar"
  >
    <div class="d-flex flex-grow-1 position-relative list-filters">
      <b-dropdown
        v-if="filters.length"
        class="mr-2 filter-menu-background"
        :variant="variant"
        data-testid="list-filter-bar-filters"
        :disabled="disabled"
      >
        <template
          #button-content
        >
          <elIcon
            class="mr-1 mb-1"
            name="filter"
          />
          {{ $t('common.filters') }}
          <elIcon
            v-if="!isFiltered"
            class="ml-2 mt-n1"
            name="chevron-down"
          />
          <div
            v-else
            class="count-circle"
            data-testid="list-filter-bar-count"
          >
            {{ filtersCount }}
          </div>
        </template>
        <div
          v-for="filter in filters"
          :key="`${filter.key}-${filter.value}`"
        >
          <label
            :for="`${filter.key}-${filter.value}`"
            class="d-flex align-items-center px-3 py-1"
          >
            <input
              :id="`${filter.key}-${filter.value}`"
              :name="`${filter.key}-${filter.value}`"
              type="checkbox"
              class="mr-2"
              data-testid="list-filter-bar-filter"
              :checked="isChecked(filter)"
              @click="apply(filter)"
              @keypress="apply(filter)"
            >
            {{ filter.label }}
          </label>
        </div>
      </b-dropdown>
      <input
        v-model="search"
        :aria-label="placeholder"
        :placeholder="placeholder"
        type="text"
        class="form-control pr-4"
        :disabled="disabled"
        data-testid="list-filter-bar-search"
        @keydown.esc="clearSearch()"
      >
      <button
        v-if="search != null && search !== ''"
        v-b-tooltip.hover.top="{
          delay: { show: 300, hide: 0 },
          title: $t('common.clear'),
          boundary: 'window'
        }"
        class="clear text-muted"
        data-testid="list-filter-bar-clear-search"
        type="button"
        :disabled="disabled"
        @click="clearSearch()"
        @keypress="clearSearch()"
      >
        <elIcon name="x" />
      </button>
      <div
        v-if="!search"
        class="clear mr-1 text-muted"
      >
        <elIcon name="search" />
      </div>
    </div>
    <div
      v-if="tags.length"
      class="d-flex mt-3"
    >
      <div
        v-for="(filter, i) in tags"
        :key="filter.label"
        class="filter-tag"
        :data-testid="`list-filter-bar-tag-${i}`"
      >
        {{ filter.label }}
        <button
          v-b-tooltip.hover.top="{
            delay: { show: 300, hide: 0 },
            title: $t('common.remove'),
            boundary: 'window'
          }"
          type="button"
          @click="apply(filter)"
          @keypress="apply(filter)"
        >
          <elIcon
            name="x"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BDropdown } from '@ui/components'
import elIcon from './elIcon.vue'
import { VBTooltip } from '@ui/components'

export default {
  directives: {
    'b-tooltip': VBTooltip
  },
  components: {
    BDropdown,
    elIcon
  },
  props: {
    stateSyncMode: {
      type: String,
      // stateSyncMode can be any one of...
      // 'query_sync' - will sync the filter state with the url query
      // 'callback_sync' - will sync the filter state via a callback
      default: 'query_sync',
      validator(value) {
        return ['query_sync', 'callback_sync'].indexOf(value) !== -1
      }
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    filters: {
      type: Array,
      default: () => []
    },
    onChange: {
      type: Function,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tags: [],
      search: '',
      urlParams: null,
      isFiltered: false,
      filtersCount: 0
    }
  },
  computed: {
    variant() {
      return this.filtersCount === 0 ? 'outline-primary' : 'primary'
    }
  },
  watch: {
    search(n) {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        if (n === '') {
          this.clearSearch()
          return
        }
        this.apply({
          key: 'search',
          value: n
        }, true)

      }, 400)
    },
    '$route.query'(query) {
      this.setUrlParams()
      this.handleQueryChanges(query)
    },
    filters: {
      handler() {
        this.handleQueryChanges(this.$route.query)
      },
      deep: true
    }
  },
  created() {
    this.setUrlParams()
    this.handleQueryChanges(this.$route.query)
  },
  methods: {
    setUrlParams() {
      this.urlParams = new URLSearchParams(window.location.search)
      const searchParam = this.urlParams.get('search')
      this.search = searchParam || ''
    },
    clearSearch() {
      this.search = ''
      switch (this.stateSyncMode) {
        case 'callback_sync':
          this.onChange({ key: 'search', value: '' })
          break
        case 'query_sync':
        default:
          this.urlParams.delete('search')
          this.$router.push(`?${this.urlParams.toString()}`)
      }
    },
    apply(filter, replace) {
      switch (this.stateSyncMode) {
        case 'callback_sync':
          this.onChange(filter)
          break
        case 'query_sync':
        default:
          if (!replace && this.urlParams.has(filter.key)) {
            const values = this.urlParams.getAll(filter.key)
            const idx = values.indexOf(filter.value)
            if (idx > -1) {
              // `.delete` will remove all params that match
              // even an array.  This will remove just the one
              // value from an array.
              this.urlParams.delete(filter.key)
              values.splice(idx, 1)
              values.forEach((v) => {
                this.urlParams.append(filter.key, v)
              })
            } else {
              this.urlParams.append(filter.key, filter.value)
            }
          } else {
            this.urlParams.set(filter.key, filter.value)
          }

          this.$router.push(`?${this.urlParams.toString()}`)
          break
      }
    },
    isChecked(filter) {
      return this.urlParams?.has(filter.key) && this.urlParams?.getAll(filter.key).includes(filter.value)
    },
    handleQueryChanges(query) {
      this.tags = []
      Object.entries(query).forEach(([key, value]) => {
        this.filters.forEach((filter) => {
          if (filter.key === key && filter.value === value
            || (Array.isArray(value) && value.includes(filter.value))) {
            this.tags.push(filter)
          }
        })
      })
      this.tags.sort((a, b) => {
        return a.label > b.label ? 1 : -1
      })
      this.filtersCount = this.tags.length
      this.isFiltered = !!this.filtersCount
    }
  }
}
</script>

<style lang="scss">
@import "~@ui/styles/src/scss/utils/lib";

.list-filters {
  .dropdown-toggle::after {
    display: none;
  }
}

.filter-tag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 5px;
  padding: 0 1px 0 5px;
  background-color: $grey-80;
  border: 1px solid $grey-70;
  border-radius: 2px;

  button {
    display: flex;
    align-items: center;
    margin-left: 2px;
    padding: 1px;
    background-color: transparent;
    border: none;
    opacity: 0.4;
  }

  &:hover button {
    opacity: 1;
  }
}

.dark .filter-tag {
  background-color: $grey-30;
  border-color: $grey-40;

  button {
    color: $grey-80;
  }

  &:hover button {
    opacity: 1;
  }
}

.count-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  margin-left: 4px;
  color: $primaryColor;
  font-size: 0.8em;
  background: white;
  border-radius: 100%;
}

.clear {
  position: absolute;
  top: 50%;
  right: 5px;
  background-color: transparent;
  border: none;
  transform: translate(0, -50%);
}

.filter-menu-background {
  background-color: white;
}

.dark .filter-menu-background {
  background-color: $grey-30;
}

</style>
